import React, { useState } from "react";
import FontIcon from "react-md/lib/FontIcons";
import moment from "moment";
import { getRiskLabel } from "../../../risk-stratification/utils/riskUtils";
import {
  AUDIT_TYPE_MAP,
  AUDIT_LEAD_CREATED,
  AUDIT_CASE_ASSIGNED,
  AUDIT_CASE_HANDED_OVER,
  AUDIT_ACTION_SET,
  AUDIT_CALL_LOGGED,
  AUDIT_FOLLOW_UP_CHANGED,
  AUDIT_CASE_CLOSED,
  AUDIT_SUMMARY_EVALUATED,
  AUDIT_APPOINTMENT_CREATED,
  AUDIT_PRESCRIPTION_LINKED,
  AUDIT_PLAN_ASSIGNED,
  AUDIT_ASSESSMENT_ADDED,
  AUDIT_CALL_RESULT_UPDATED,
  CALL_MISSED_BY_LABEL_MAP,
  OUTCOME_LABEL_MAP,
  ROLE_LABEL_MAP,
  CALL_ANSWERED,
  CALL_MISSED,
  LTR_EVALUATION_INCORRECT,
} from "../../constants";
import { talkTime } from "./CallAudit";

const TYPE_CLASS = {
  Task: "type-task",
  Update: "type-update",
  Call: "type-call",
  Action: "type-action",
  Milestone: "type-milestone",
};

const fmt = (d) => (d ? moment(d).format("D MMM, h:mm A") : "-");

// One activity-log row's headline and optional second line.
export function describeAudit(a) {
  const m = a.metadata || {};
  const label = a.label || a.eventType;
  switch (a.eventType) {
    case AUDIT_LEAD_CREATED:
      return { title: "Lead created from Prism stratification" };
    case AUDIT_CASE_ASSIGNED:
      return {
        title: `Case assigned to ${a.subjectName || "-"}${m.followUpDate ? ` — follow up by ${fmt(m.followUpDate)}` : ""}`,
        detail: m.task,
      };
    case AUDIT_CASE_HANDED_OVER:
      return {
        title: `Task assigned${m.task ? ` — ${m.task}` : ""}`,
        detail: m.followUpDate ? `Follow up by ${fmt(m.followUpDate)}` : undefined,
      };
    case AUDIT_ACTION_SET:
      return { title: m.labels ? `Action set · ${m.labels}` : "Actions cleared", detail: m.note };
    case AUDIT_CALL_LOGGED: {
      if (m.outcome === CALL_ANSWERED)
        return { title: `Call answered — ${talkTime(m.durationSeconds)}${m.remark ? ` · ${m.remark}` : ""}` };
      const missedBy = CALL_MISSED_BY_LABEL_MAP[m.missedBy];
      if (m.outcome === CALL_MISSED)
        return {
          title: `Call missed${missedBy ? ` — ${missedBy.toLowerCase()}` : ""}${
            m.remark ? `${missedBy ? " · " : " — "}${m.remark}` : ""
          }`,
        };
      return { title: "Call attempted" };
    }
    case AUDIT_CALL_RESULT_UPDATED: {
      const missedBy = CALL_MISSED_BY_LABEL_MAP[m.missedBy];
      const now =
        m.outcome === CALL_ANSWERED
          ? `answered — ${talkTime(m.durationSeconds)}`
          : `missed${missedBy ? ` — ${missedBy.toLowerCase()}` : ""}`;
      const before =
        m.previousOutcome === CALL_ANSWERED
          ? `answered, ${talkTime(m.previousDurationSeconds)}`
          : m.previousOutcome || "-";
      return { title: `Call ${m.callId} corrected by SparkTG: ${now}`, detail: `The floater had recorded it as ${before}` };
    }
    case AUDIT_FOLLOW_UP_CHANGED:
      return { title: `Follow-up moved → ${fmt(m.followUpDate)}` };
    case AUDIT_CASE_CLOSED:
      return {
        title: `Case closed — ${(m.label || OUTCOME_LABEL_MAP[m.outcome] || "").toLowerCase()}`,
        detail: m.reason,
      };
    case AUDIT_SUMMARY_EVALUATED:
      return m.evaluationStatus === LTR_EVALUATION_INCORRECT
        ? {
            title: "AI risk summary evaluated — marked incorrect, evaluator summary written",
            detail: m.updatedRiskLevel ? `Risk restated as ${getRiskLabel(m.updatedRiskLevel)}` : undefined,
          }
        : { title: "AI risk summary evaluated — marked correct" };
    case AUDIT_APPOINTMENT_CREATED:
      return {
        title: `Appointment ${m.appointmentId || ""} created${m.callId ? ` from call ${m.callId}` : ""}`,
        detail: [m.mode, m.serviceName].filter(Boolean).join(" · ") || undefined,
      };
    case AUDIT_PRESCRIPTION_LINKED:
      return {
        title: `Rx ${m.prescriptionId || ""} issued${m.appointmentId ? ` against appointment ${m.appointmentId}` : ""}`,
      };
    case AUDIT_PLAN_ASSIGNED:
      return {
        title: "Patient assigned to the LTR plan",
        detail: m.accountCode ? `${m.accountCode} · plan ${m.planId}` : undefined,
      };
    case AUDIT_ASSESSMENT_ADDED: {
      const reasons = Array.isArray(m.riskLevelTriggeredBy) ? m.riskLevelTriggeredBy.join(", ") : "";
      return m.current === false
        ? { title: "Earlier Prism assessment linked to this case", detail: reasons || undefined }
        : {
            title: "New Prism assessment — case updated, AI summary to re-evaluate",
            detail: reasons ? `Triggered by ${reasons}` : undefined,
          };
    }
    default:
      return { title: label };
  }
}

/**
 * Activity log: every case milestone and every update in one audit, newest
 * first. Milestone rows are the case's status timeline.
 */
function ActivityLog({ audits, roleById, onViewEvaluation, canViewEvaluation }) {
  const [open, setOpen] = useState(true);
  const rows = [...(audits || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const roleOf = (id) => (roleById[id] ? ROLE_LABEL_MAP[roleById[id]] : "");

  return (
    <section className="ltr-detail-card">
      <div className="ltr-collapse-head">
        <div>
          <h3>Activity log</h3>
          <p className="ltr-card-sub">
            Every case milestone and every update in one audit — the status timeline is the “Milestone” rows.
          </p>
        </div>
        <span className="ltr-collapse-right">
          <span className="ltr-count-chip">
            {rows.length} entr{rows.length === 1 ? "y" : "ies"}
          </span>
          <button
            type="button"
            className="ltr-collapse-icon"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? "Collapse activity log" : "Expand activity log"}
          >
            <FontIcon>{open ? "expand_less" : "expand_more"}</FontIcon>
          </button>
        </span>
      </div>
      {open && (
        <div className="ltr-table-wrap">
          <table className="ltr-table ltr-activity-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Event</th>
                <th>Type</th>
                <th>Actor</th>
                <th>Where</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="ltr-table-empty">
                    No activity yet.
                  </td>
                </tr>
              ) : (
                rows.map((a, idx) => {
                  const meta = AUDIT_TYPE_MAP[a.eventType] || { type: "Update", source: "Bridge" };
                  const { title, detail } = describeAudit(a);
                  const isSystem = !a.actorEmployeeId;
                  const handover =
                    (a.eventType === AUDIT_CASE_HANDED_OVER || a.eventType === AUDIT_CASE_ASSIGNED) &&
                    a.subjectEmployeeId &&
                    a.subjectEmployeeId !== a.actorEmployeeId;
                  const fromSparktg = a.eventType === AUDIT_CALL_RESULT_UPDATED;
                  let actorName = a.actorName || `Employee ${a.actorEmployeeId}`;
                  if (isSystem) actorName = fromSparktg ? "SparkTG" : "Prism";
                  let actorRole = roleOf(a.actorEmployeeId);
                  if (isSystem) actorRole = fromSparktg ? "Call record" : "AI system";
                  return (
                    <tr key={a.id || idx} className={TYPE_CLASS[meta.type]}>
                      <td className="ltr-when-cell">
                        <span className="ltr-datetime">
                          <span className="ltr-datetime-date">{moment(a.createdAt).format("D MMM YYYY")}</span>
                          <span className="ltr-datetime-time">{moment(a.createdAt).format("h:mm A")}</span>
                        </span>
                      </td>
                      <td>
                        <div className="ltr-strong">{title}</div>
                        {detail && <div className="ltr-cell-sub">{detail}</div>}
                        {a.eventType === AUDIT_SUMMARY_EVALUATED && canViewEvaluation && (
                          <button type="button" className="ltr-text-btn" onClick={onViewEvaluation}>
                            View evaluation
                          </button>
                        )}
                      </td>
                      <td>
                        <span className={`ltr-type-pill ${TYPE_CLASS[meta.type]}`}>{meta.type}</span>
                      </td>
                      <td>
                        <div className="ltr-strong">
                          {handover ? `${actorName} → ${a.subjectName || "-"}` : actorName}
                        </div>
                        <div className="ltr-cell-sub">
                          {handover
                            ? `${actorRole || "-"} → ${roleOf(a.subjectEmployeeId) || "-"}`
                            : actorRole}
                        </div>
                      </td>
                      <td className="ltr-muted">{isSystem ? "Prism · automated" : meta.source}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default ActivityLog;
